import React, { useState, useEffect } from "react";
import styles from "./PlayerScoreBoard.module.css";
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { setPlayerStats } from '../../../../store/playerSlice';
import { authHeader } from '../../../../services/authHeader';
import BackButton from '../../../Shared/BackButton';
import StatRow from '../../../Shared/StatRow';
import CupBoard from '../../../Shared/CupBoard/CupBoard';
import RecentGameRow from './RecentGameRow';

const PlayerScoreBoard = () => {
  const username = useSelector((state) => state.auth.username || state.user.username);
  const player = useSelector((state) => state.player);
  const dispatch = useDispatch();
  const location = useLocation();
  const { cups, games } = location.state || {};

  const initialStats = {
    gamesPlayed: player.totalGamesCompleted,
    wins: player.wins,
    losses: player.losses,
    totalCupsHit: player.totalCupsFor,
    totalCupsAllowed: player.totalCupsAgainst,
    totalLapsGiven: player.lapsGiven,
    totalLapsReceived: player.lapsReceived,
  };

  const [stats, setStats] = useState(initialStats);
  const [cupValues, setCupValues] = useState(cups);
  const [recentGames, setRecentGames] = useState(games);
  const [expandedGame, setExpandedGame] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!username) return;
    const needsPlayerStats = player.cups.length < 10;
    const needsTeamStats = !cups;
    if (!needsPlayerStats && !needsTeamStats) return;

    if (needsPlayerStats) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/c/getstats/`, { params: { username } })
        .then(response => {
          const p = response.data.player_stats || {};
          dispatch(setPlayerStats({
            totalGamesPlayed: p.total_games_played,
            totalGamesCompleted: p.total_games_completed,
            wins: p.wins,
            losses: p.losses,
            winningPercentage: p.winning_percentage,
            saves: p.saves,
            savesPerGame: p.saves_per_game,
            halfcups: p.half_cups,
            halfcupsPerGame: p.half_cups_per_game,
            fullCups: p.full_cups,
            fullCupsPerGame: p.full_cups_per_game,
            ownCups: p.own_cups,
            ownCupsPerGame: p.own_cups_per_game,
            totalCups: p.total_cups,
            totalCupsPerGame: p.total_cups_per_game,
            lapsGiven: p.laps_given,
            lapsReceived: p.laps_received,
            totalCupsFor: p.total_cups_for,
            totalCupsAgainst: p.total_cups_against,
            cups: p.cups,
          }));
          setStats({
            gamesPlayed: p.total_games_completed,
            wins: p.wins,
            losses: p.losses,
            totalCupsHit: p.total_cups_for,
            totalCupsAllowed: p.total_cups_against,
            totalLapsGiven: p.laps_given,
            totalLapsReceived: p.laps_received,
          });
        })
        .catch(() => {});
    }

    if (needsTeamStats) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/c/getteamstats/`, { params: { username } })
        .then(response => {
          const c = response.data.stats.cups;
          setCupValues({
            off_fr: c.off_fr?.value ?? 0,
            off_lf: c.off_lf?.value ?? 0,
            off_rt: c.off_rt?.value ?? 0,
            off_ct: c.off_ct?.value ?? 0,
            off_bk: c.off_bk?.value ?? 0,
            def_fr: c.def_fr?.value ?? 0,
            def_lf: c.def_lf?.value ?? 0,
            def_rt: c.def_rt?.value ?? 0,
            def_ct: c.def_ct?.value ?? 0,
            def_bk: c.def_bk?.value ?? 0,
          });
          setRecentGames(response.data.stats.recentGames ?? []);
        })
        .catch(() => {});
    }
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-filter search state
  const [activeFilter, setActiveFilter] = useState(null); // 'teammate' | 'opp1' | 'opp2'
  const [queries, setQueries] = useState({ teammate: '', opp1: '', opp2: '' });
  const [results, setResults] = useState({ teammate: [], opp1: [], opp2: [] });
  const [selected, setSelected] = useState({ teammate: null, opp1: null, opp2: null });

  const updateStats = async (teammate, opp1, opp2) => {
    setErrorMessage(null);
    try {
      if (!teammate && !opp1 && !opp2) {
        setStats(initialStats);
        setCupValues(cups);
        setRecentGames(games);
        return;
      }
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/c/getteamstats/`, {
        params: {
          username: username,
          teammate: teammate?.username,
          opponent1: opp1?.username,
          opponent2: opp2?.username,
        },
      });
      setStats({
        gamesPlayed: response.data.stats.gamesPlayed,
        wins: response.data.stats.wins,
        losses: response.data.stats.losses,
        totalCupsHit: response.data.stats.totalCupsHit,
        totalCupsAllowed: response.data.stats.totalCupsAllowed,
        totalLapsGiven: response.data.stats.totalLapsGiven,
        totalLapsReceived: response.data.stats.totalLapsReceived,
      });
      setCupValues({
        off_fr: response.data.stats.cups.off_fr.value,
        off_lf: response.data.stats.cups.off_lf.value,
        off_rt: response.data.stats.cups.off_rt.value,
        off_ct: response.data.stats.cups.off_ct.value,
        off_bk: response.data.stats.cups.off_bk.value,
        def_fr: response.data.stats.cups.def_fr.value,
        def_lf: response.data.stats.cups.def_lf.value,
        def_rt: response.data.stats.cups.def_rt.value,
        def_ct: response.data.stats.cups.def_ct.value,
        def_bk: response.data.stats.cups.def_bk.value,
      });
      setRecentGames(response.data.stats.recentGames);
    } catch (err) {
      setErrorMessage("No games found with these filters.");
      setStats({ gamesPlayed: 0, wins: 0, losses: 0, totalCupsHit: 0, totalCupsAllowed: 0, totalLapsGiven: 0, totalLapsReceived: 0 });
      setCupValues({ off_fr: 0, off_lf: 0, off_rt: 0, off_ct: 0, off_bk: 0, def_fr: 0, def_lf: 0, def_rt: 0, def_ct: 0, def_bk: 0 });
      setRecentGames([]);
    }
  };

  const handleSearch = async (key) => {
    const q = queries[key];
    if (!q.trim()) return;

    if (q.trim().toLowerCase() === username.toLowerCase()) {
      setErrorMessage("That's you! You can't filter by yourself.");
      return;
    }

    setErrorMessage(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/c/searchusername`, {
        params: { username: q },
        ...authHeader(),
      });
      setResults(prev => ({ ...prev, [key]: response.data.suggestions || [] }));
    } catch (err) {
      const msg = err?.response?.data?.error;
      setErrorMessage(msg || "Error fetching search results.");
    }
  };

  const handleSelect = (key, player) => {
    const next = { ...selected, [key]: player };
    setSelected(next);
    setResults(prev => ({ ...prev, [key]: [] }));
    setQueries(prev => ({ ...prev, [key]: '' }));
    setActiveFilter(null);
    updateStats(next.teammate, next.opp1, next.opp2);
  };

  const handleClear = (key) => {
    const next = { ...selected, [key]: null };
    setSelected(next);
    setActiveFilter(null);
    updateStats(next.teammate, next.opp1, next.opp2);
  };

  const avgFor = stats.gamesPlayed > 0 ? (stats.totalCupsHit / stats.gamesPlayed).toFixed(2) : '0.00';
  const avgAgainst = stats.gamesPlayed > 0 ? (stats.totalCupsAllowed / stats.gamesPlayed).toFixed(2) : '0.00';

  const filterDefs = [
    { key: 'teammate', label: 'Teammate' },
    { key: 'opp1', label: 'Opponent 1' },
    { key: 'opp2', label: 'Opponent 2' },
  ];

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Advanced Stats</h1>

      <div className={styles.content}>

        {/* Filters */}
        <div className={styles.filterCard}>
          <p className={styles.filterTitle}>Filter by Player</p>
          {filterDefs.map(({ key, label }) => (
            <div key={key}>
              <div className={styles.filterRow}>
                <span className={styles.filterLabel}>{label}</span>
                <span className={styles.filterValue}>
                  {selected[key] ? selected[key].username : 'Anyone'}
                </span>
                <div className={styles.filterActions}>
                  {selected[key] ? (
                    <button className={styles.filterBtn} onClick={() => handleClear(key)}>✕ Clear</button>
                  ) : (
                    <button
                      className={styles.filterBtn}
                      onClick={() => setActiveFilter(activeFilter === key ? null : key)}
                    >
                      {activeFilter === key ? 'Cancel' : '+ Add'}
                    </button>
                  )}
                </div>
              </div>
              {activeFilter === key && !selected[key] && (
                <div>
                  <div className={styles.filterSearch}>
                    <input
                      className={styles.filterInput}
                      type="text"
                      value={queries[key]}
                      onChange={(e) => setQueries(prev => ({ ...prev, [key]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch(key)}
                      placeholder="Search username…"
                    />
                    <button className={styles.searchGoBtn} onClick={() => handleSearch(key)}>Search</button>
                  </div>
                  {results[key].length > 0 && (
                    <div className={styles.searchResults}>
                      {results[key].map((p, i) => (
                        <div key={i} className={styles.searchItem} onClick={() => handleSelect(key, p)}>
                          {p.username}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        </div>

        {/* Avg score */}
        <div className={styles.avgScore}>
          Avg Game
          <span className={styles.avgScoreValue}>
            {username} & {selected.teammate?.username || 'Anyone'} &nbsp;{avgFor} – {avgAgainst}&nbsp; {selected.opp1?.username || 'Anyone'} & {selected.opp2?.username || 'Anyone'}
          </span>
        </div>

        {/* Shot chart */}
        <div className={styles.cupBoardWrapper}>
          <div className={styles.boardHeader}>
            <span className={styles.boardHeaderLabel}>{selected.opp1?.username || 'Any Opp'}</span>
            <span className={styles.boardHeaderLabel}>{selected.opp2?.username || 'Any Opp'}</span>
          </div>
          <CupBoard
            variant="full"
            showHeat
            attackData={{
              fr: cupValues?.off_fr,
              lf: cupValues?.off_lf,
              rt: cupValues?.off_rt,
              ct: cupValues?.off_ct,
              bk: cupValues?.off_bk,
            }}
            defenseData={{
              fr: cupValues?.def_fr,
              lf: cupValues?.def_lf,
              rt: cupValues?.def_rt,
              ct: cupValues?.def_ct,
              bk: cupValues?.def_bk,
            }}
          />
          <div className={styles.boardFooterLabels}>
            <span className={styles.boardHeaderLabel}>{username}</span>
            <span className={styles.boardHeaderLabel}>{selected.teammate?.username || 'Any Teammate'}</span>
          </div>
        </div>

        {/* Game stats */}
        <div className={styles.statsSection}>
          <p className={styles.sectionTitle}>Game Stats</p>
          <StatRow label="Games Played" value={stats.gamesPlayed} />
          <StatRow label="Wins" value={stats.wins} />
          <StatRow label="Losses" value={stats.losses} />
          <StatRow label="Total Cups Hit" value={stats.totalCupsHit} />
          <StatRow label="Total Cups Allowed" value={stats.totalCupsAllowed} />
          <StatRow label="Laps Given" value={stats.totalLapsGiven} />
          <StatRow label="Laps Received" value={stats.totalLapsReceived} />
        </div>

        {/* Last 10 games */}
        <div className={styles.statsSection}>
          <p className={styles.sectionTitle}>Last 10 Games</p>
          <div>
            {recentGames &&
              Object.entries(recentGames)
                .filter(([_, game]) => game && Object.keys(game).length > 0)
                .map(([key, game]) => (
                  <RecentGameRow
                    key={key}
                    game={game}
                    username={username}
                    expanded={expandedGame === key}
                    onToggle={() => setExpandedGame(expandedGame === key ? null : key)}
                  />
                ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PlayerScoreBoard;
